import * as React from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.visible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  position: relative;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 5px;
  line-height: 1;
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
`;

const Icon = styled.div`
  text-align: center;
  margin-bottom: 20px;

  i {
    font-size: 64px;
    color: #ff9800;
  }
`;

const Title = styled.h2`
  text-align: center;
  color: #333;
  margin: 0 0 15px 0;
  font-size: 28px;
`;

const Message = styled.p`
  text-align: center;
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 25px 0;
`;

const OfferBox = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  margin-bottom: 25px;
`;

const OfferTitle = styled.div`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const OfferDescription = styled.div`
  font-size: 14px;
  opacity: 0.95;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(118, 75, 162, 0.4);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #999;
  border: none;
  padding: 10px;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #666;
  }
`;

interface ExitIntentModalProps {
  onStay: () => void;
  onLeave: () => void;
}

export const ExitIntentModal: React.FC<ExitIntentModalProps> = ({ onStay, onLeave }) => {
  const [visible, setVisible] = React.useState(false);
  const [hasShown, setHasShown] = React.useState(false);

  React.useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShown) {
        setVisible(true);
        setHasShown(true);
        
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'exit_intent_shown',
          timestamp: new Date().toISOString(),
        });
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasShown]);

  const handleStay = () => {
    setVisible(false);
    onStay();
    
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'exit_intent_stay',
      timestamp: new Date().toISOString(),
    });
  };

  const handleLeave = () => {
    setVisible(false);
    onLeave();
    
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'exit_intent_leave',
      timestamp: new Date().toISOString(),
    });
  };

  return (
    <ModalOverlay visible={visible}>
      <ModalContent>
        <CloseButton onClick={handleLeave}>×</CloseButton>
        
        <Icon>
          <i className="material-icons">priority_high</i>
        </Icon>
        
        <Title>Espere! Não vá embora ainda...</Title>
        
        <Message>
          Você está a um passo de transformar sua saúde e alcançar seus objetivos. 
          Milhares de pessoas já mudaram suas vidas com nossos treinos personalizados.
        </Message>
        
        <OfferBox>
          <OfferTitle>🎁 Oferta Especial</OfferTitle>
          <OfferDescription>
            Complete seu cadastro agora e ganhe 7 dias de garantia incondicional. 
            Se não gostar, devolvemos seu dinheiro!
          </OfferDescription>
        </OfferBox>
        
        <ButtonGroup>
          <PrimaryButton onClick={handleStay}>
            Quero Aproveitar Esta Oferta
          </PrimaryButton>
          <SecondaryButton onClick={handleLeave}>
            Não, obrigado. Prefiro sair.
          </SecondaryButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

